import { Button } from "@aurumiq/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@aurumiq/ui/components/dialog";
import { CopyButton } from "@aurumiq/ui/composite/copy-button";
import { ReasonDialog } from "@aurumiq/ui/composite/reason-dialog";
import { dmy } from "@aurumiq/ui/lib/format";
import { Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { usePermission } from "@/auth/use-permission";
import { TextField } from "@/components/field";
import { formatBytes } from "@/features/releases/artifacts";
import type { CmsMedia } from "@/lib/types";
import { MediaThumb } from "./media-picker";
import { useDeleteMedia, usePatchMedia } from "./queries";

function AltDialog({
  media,
  onOpenChange,
}: {
  media: CmsMedia | null;
  onOpenChange: (o: boolean) => void;
}) {
  const patch = usePatchMedia();
  const [alt, setAlt] = useState("");
  return (
    <Dialog
      open={media !== null}
      onOpenChange={(o) => {
        if (o && media) setAlt(media.alt);
        onOpenChange(o);
      }}
    >
      <DialogContent>
        <form
          className="grid gap-4"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!media) return;
            await patch.mutateAsync({ id: media.id, alt });
            onOpenChange(false);
          }}
        >
          <DialogHeader>
            <DialogTitle>Alt text for {media?.filename}</DialogTitle>
          </DialogHeader>
          <TextField
            label="Alt text"
            autoFocus
            maxLength={300}
            defaultValue={media?.alt ?? ""}
            onChange={(e) => setAlt(e.target.value)}
            hint="Describe the image for screen readers and SEO."
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={patch.isPending}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function MediaGrid({ items }: { items: CmsMedia[] }) {
  const canWrite = usePermission("cms:write");
  const del = useDeleteMedia();
  const [editing, setEditing] = useState<CmsMedia | null>(null);
  const [deleting, setDeleting] = useState<CmsMedia | null>(null);
  if (!items.length)
    return (
      <p className="rounded-xl border border-dashed p-6 text-center text-muted-foreground text-sm">
        No media yet.
      </p>
    );
  return (
    <>
      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {items.map((m) => (
          <li key={m.id} className="grid gap-1.5 rounded-xl border bg-card p-2">
            <MediaThumb media={m} className="aspect-video w-full rounded-lg object-cover" />
            <div className="truncate font-medium text-sm" title={m.filename}>
              {m.filename}
            </div>
            <div className="text-muted-foreground text-xs">
              {m.width && m.height ? `${m.width}×${m.height} · ` : ""}
              {formatBytes(m.size)} · {dmy(m.created_at)}
            </div>
            <div className="truncate text-xs" title={m.alt}>
              {m.alt ? m.alt : <span className="text-warning">No alt text</span>}
            </div>
            <div className="flex items-center gap-1">
              <CopyButton value={m.url} label="URL" />
              {canWrite ? (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7"
                    aria-label={`Edit alt for ${m.filename}`}
                    onClick={() => setEditing(m)}
                  >
                    <Pencil />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7 text-destructive"
                    aria-label={`Delete ${m.filename}`}
                    onClick={() => setDeleting(m)}
                  >
                    <Trash2 />
                  </Button>
                </>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
      <AltDialog media={editing} onOpenChange={(o) => !o && setEditing(null)} />
      <ReasonDialog
        open={deleting !== null}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={`Delete ${deleting?.filename}?`}
        description="Pages that reference this file will show a broken image."
        confirmLabel="Delete"
        onConfirm={async (reason) => {
          if (deleting) await del.mutateAsync({ id: deleting.id, reason });
        }}
      />
    </>
  );
}
